// Playback clock of the Studio timeline: an external store OUTSIDE React
// state. Playing advances it with requestAnimationFrame; subscribers (the
// canvas animation driver, the playhead, the time readout) update the DOM
// imperatively, so a playing timeline never re-renders the Studio per frame.
// Only play/pause changes notify React (useClockPlaying).

import { useEffect, useSyncExternalStore, type RefObject } from "react";

export class PlaybackClock {
  #time = 0;
  #playing = false;
  #span = 5_000;
  #raf = 0;
  #last = 0;
  readonly #tick = new Set<(t: number) => void>();
  readonly #status = new Set<() => void>();

  get time() {
    return this.#time;
  }
  get playing() {
    return this.#playing;
  }
  get span() {
    return this.#span;
  }

  /** Visible length of the current scene's timeline (ms). */
  setSpan(ms: number) {
    this.#span = Math.max(1, ms);
    if (this.#time > this.#span) this.seek(this.#span);
  }

  seek(ms: number) {
    this.#time = Math.round(Math.min(this.#span, Math.max(0, ms)));
    for (const cb of this.#tick) cb(this.#time);
  }

  play() {
    if (this.#playing) return;
    if (this.#time >= this.#span) this.seek(0);
    this.#playing = true;
    this.#last = performance.now();
    const step = (now: number) => {
      if (!this.#playing) return;
      // Long frames (background tab) are capped, so playback never jumps far.
      const dt = Math.min(100, now - this.#last);
      this.#last = now;
      const next = this.#time + dt;
      if (next >= this.#span) {
        this.seek(this.#span);
        this.pause();
        return;
      }
      this.#time = next;
      for (const cb of this.#tick) cb(this.#time);
      this.#raf = requestAnimationFrame(step);
    };
    this.#raf = requestAnimationFrame(step);
    this.#notify();
  }

  pause() {
    if (!this.#playing) return;
    this.#playing = false;
    cancelAnimationFrame(this.#raf);
    this.#notify();
  }

  toggle() {
    if (this.#playing) this.pause();
    else this.play();
  }

  restart() {
    this.pause();
    this.seek(0);
  }

  onTick(cb: (t: number) => void) {
    this.#tick.add(cb);
    return () => {
      this.#tick.delete(cb);
    };
  }

  onStatus = (cb: () => void) => {
    this.#status.add(cb);
    return () => {
      this.#status.delete(cb);
    };
  };

  #notify() {
    for (const cb of this.#status) cb();
  }
}

export function useClockPlaying(clock: PlaybackClock) {
  return useSyncExternalStore(
    clock.onStatus,
    () => clock.playing,
    () => false
  );
}

/** Writes the formatted clock time into a node on every tick (no React state). */
export function useClockText(clock: PlaybackClock, ref: RefObject<HTMLElement | null>, format: (t: number) => string) {
  useEffect(() => {
    const write = (t: number) => {
      if (ref.current) ref.current.textContent = format(t);
    };
    write(clock.time);
    return clock.onTick(write);
  }, [clock, ref, format]);
}

export const formatSeconds = (ms: number) => `${(ms / 1_000).toFixed(2)} s`;
